import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'core-form-list',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [formGroup]="formGroup">
      <div [ngClass]="option.listClass" [formArrayName]="formArrayName">
        @for (control of items.controls; track $index) {
          <div [ngClass]="option.itemClass" data-testid="item">
            @if (itemTemplate) {
              <ng-container
                *ngTemplateOutlet="
                  itemTemplate;
                  context: { control, index: $index }
                "
              ></ng-container>
            } @else {
              <ng-container
                *ngTemplateOutlet="
                  defaultTemplate;
                  context: { control, index: $index }
                "
              ></ng-container>
            }
          </div>
        }
      </div>
    </div>
    <ng-template #defaultTemplate let-control="control">
      {{ control.value }}
    </ng-template>
  `,
  styles: [``],
})
export class FormListComponent<TControl extends AbstractControl<unknown>>
  implements OnInit
{
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly formGroupDirective = inject(FormGroupDirective);

  protected _defaultOption: FormListOption = {
    listClass: '',
    itemClass: '',
  };
  @Input()
  public itemTemplate?: TemplateRef<unknown>;
  @Input()
  public set option(option: FormListOption) {
    this._option = Object.assign(option, this._defaultOption, {
      ...option,
    });
  }
  public get option(): FormListOption {
    return this._option;
  }
  private _option: FormListOption = this._defaultOption;
  protected formGroup!: FormGroup;
  @Input({ required: true })
  public formArrayName!: string;
  protected get items() {
    return this.formGroup.get(this.formArrayName) as FormArray;
  }
  private readonly destroyRef = inject(DestroyRef);

  protected add(control: TControl): void {
    this.items.push(control);
  }

  public ngOnInit(): void {
    this.formGroup = this.formGroupDirective.form;
    this.items?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.detectChanges());
  }

  protected getAt(index: number) {
    return this.items.at(index) as TControl;
  }

  protected removeAt(index: number) {
    this.items.removeAt(index);
  }

  protected clear() {
    this.items.clear();
  }

  public get length() {
    return this.items.length;
  }
}

export interface FormListOption {
  listClass?: string;
  itemClass?: string;
}
